/* MSG91 OTP Diagnostic — admin-only.
 *
 * Surfaces the EXACT MSG91 API response (HTTP status, error message,
 * raw body, template_id used, sender used, auth-key length) so we can
 * pinpoint DLT template approval / sender-ID mismatches without
 * tailing supervisor logs.
 *
 * Backed by:
 *   POST /api/admin/sms/test     · forces a real MSG91 send
 *   GET  /api/admin/msg91-logs   · last 50 attempts (persisted, 30-day TTL)
 */
import React, { useState, useCallback, useEffect } from "react";
import { Send, MessageSquareWarning, RefreshCw, CheckCircle2, AlertTriangle } from "lucide-react";
import { adminGet, adminPost } from "@/lib/adminApi";

const Pill = ({ ok, children }) => (
  <span
    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-extrabold uppercase tracking-wider text-[10px] ${
      ok
        ? "bg-emerald-100 text-emerald-700"
        : "bg-red-100 text-red-700"
    }`}
  >
    {ok ? (
      <CheckCircle2 className="h-3 w-3" />
    ) : (
      <AlertTriangle className="h-3 w-3" />
    )}
    {children}
  </span>
);

const Msg91DiagnosticPanel = () => {
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("654321");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsError, setLogsError] = useState(null);

  const loadLogs = useCallback(async () => {
    setLogsLoading(true);
    setLogsError(null);
    const res = await adminGet("/admin/msg91-logs?limit=50");
    if (res.ok) {
      setLogs(res.data?.items || []);
    } else {
      setLogs([]);
      setLogsError(res.error || `HTTP ${res.status}`);
    }
    setLogsLoading(false);
  }, []);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const handleSend = useCallback(async () => {
    const trimmed = (phone || "").replace(/\D+/g, "");
    if (trimmed.length < 10) {
      setResult({ ok: false, error: "Enter a valid 10-digit Indian mobile number." });
      return;
    }
    setSending(true);
    setResult(null);
    const res = await adminPost("/admin/sms/test", { phone: trimmed.slice(-10), code });
    if (res.ok) {
      setResult(res.data);
    } else {
      setResult({ ok: false, error: res.error || `HTTP ${res.status}`, status: res.status });
    }
    setSending(false);
    // Refresh history so the new attempt is visible.
    loadLogs();
  }, [phone, code, loadLogs]);

  return (
    <section
      className="mb-8 rounded-3xl bg-white border-2 border-navy/15 shadow-[0_24px_48px_-24px_rgba(11,37,69,0.20)] overflow-hidden"
      data-testid="admin-msg91-diagnostic"
    >
      <header className="bg-gradient-to-r from-red-900 via-red-800 to-red-900 text-white p-5 flex items-center gap-3 relative">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-300 via-amber-200 to-amber-300" />
        <div className="h-12 w-12 rounded-2xl bg-amber-300 text-red-900 grid place-items-center shrink-0 shadow-md">
          <MessageSquareWarning className="h-6 w-6" strokeWidth={2.4} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-[0.22em] text-amber-200 font-bold">
            MSG91 · Live Diagnostic
          </div>
          <h2 className="text-xl font-extrabold tracking-tight">
            OTP Delivery Test &amp; Recent Attempts
          </h2>
          <p className="text-[11.5px] text-amber-100 mt-0.5">
            Sends a real OTP via MSG91 and shows the EXACT API response — including DLT template + sender-ID errors.
          </p>
        </div>
      </header>

      <div className="p-5 sm:p-6 space-y-5">
        {/* Test form */}
        <div className="rounded-2xl border-2 border-navy/15 p-4 bg-navy/[0.02]">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[180px]">
              <label className="block text-[11px] font-extrabold uppercase tracking-wider text-navy/70 mb-1">
                Mobile number (10 digits)
              </label>
              <input
                type="tel"
                inputMode="numeric"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="9876543210"
                className="w-full h-11 px-3 rounded-xl border-2 border-navy/15 focus:border-navy outline-none font-mono text-[14px]"
                data-testid="msg91-test-phone-input"
              />
            </div>
            <div className="w-[120px]">
              <label className="block text-[11px] font-extrabold uppercase tracking-wider text-navy/70 mb-1">
                Test code
              </label>
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className="w-full h-11 px-3 rounded-xl border-2 border-navy/15 focus:border-navy outline-none font-mono text-[14px]"
                data-testid="msg91-test-code-input"
              />
            </div>
            <button
              onClick={handleSend}
              disabled={sending}
              data-testid="msg91-test-send-btn"
              className="h-11 px-5 rounded-xl bg-navy text-gold font-extrabold text-[13px] inline-flex items-center gap-2 hover:bg-navy-deep disabled:opacity-60"
            >
              <Send className="h-4 w-4" />
              {sending ? "Sending…" : "Send test OTP"}
            </button>
          </div>

          {result && (
            <div
              className={`mt-4 rounded-xl border-2 p-3 ${
                result.ok ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"
              }`}
              data-testid="msg91-test-result"
            >
              <div className="flex items-center gap-2 mb-2">
                <Pill ok={!!result.ok}>{result.ok ? "Delivered" : "Failed"}</Pill>
                {result.status != null && (
                  <span className="text-[11px] font-mono text-navy/70">
                    HTTP {result.status}
                  </span>
                )}
                {result.provider && (
                  <span className="text-[11px] font-mono text-navy/70">
                    provider={result.provider}
                  </span>
                )}
              </div>
              {result.error && (
                <div className="text-[12.5px] text-red-900 font-bold mb-2" data-testid="msg91-test-error">
                  {result.error}
                </div>
              )}
              <details open className="text-[11px]">
                <summary className="cursor-pointer font-extrabold text-navy">
                  Raw response
                </summary>
                <pre className="mt-1 whitespace-pre-wrap break-all max-h-[280px] overflow-auto bg-white rounded-lg p-2 border border-navy/10 font-mono text-[10.5px] leading-snug">
                  {JSON.stringify(result, null, 2)}
                </pre>
              </details>
            </div>
          )}
        </div>

        {/* History */}
        <div className="rounded-2xl border-2 border-navy/15 p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-[14px] font-extrabold text-navy">
                Recent OTP attempts (last 50)
              </h3>
              <p className="text-[11px] text-muted-foreground">
                Persisted with 30-day retention. Sensitive values (OTP code, auth-key) are never stored.
              </p>
            </div>
            <button
              onClick={loadLogs}
              disabled={logsLoading}
              data-testid="msg91-logs-refresh"
              className="h-9 px-3 rounded-lg bg-white border-2 border-navy text-navy font-extrabold text-[11.5px] inline-flex items-center gap-1.5 hover:bg-navy/5 disabled:opacity-60"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${logsLoading ? "animate-spin" : ""}`} />
              Refresh
            </button>
          </div>

          {logsError && (
            <div className="text-[12px] text-red-700 font-bold mb-2">
              Could not load history: {logsError}
            </div>
          )}

          {logs.length === 0 ? (
            <div className="text-[12px] text-muted-foreground italic" data-testid="msg91-logs-empty">
              {logsLoading ? "Loading…" : "No attempts logged yet. Run a test send above."}
            </div>
          ) : (
            <div className="overflow-x-auto" data-testid="msg91-logs-table">
              <table className="w-full text-[11.5px]">
                <thead>
                  <tr className="text-left text-navy/60 uppercase text-[10px] tracking-wider border-b border-navy/10">
                    <th className="py-2 pr-3">When</th>
                    <th className="py-2 pr-3">Phone</th>
                    <th className="py-2 pr-3">Ctx</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2 pr-3">Error</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((row) => (
                    <tr
                      key={row.id}
                      className="border-b border-navy/5 hover:bg-navy/[0.02]"
                      data-testid={`msg91-log-row-${row.id}`}
                    >
                      <td className="py-1.5 pr-3 font-mono text-[10.5px] text-navy/70">
                        {row.created_at?.replace("T", " ").slice(0, 19)}
                      </td>
                      <td className="py-1.5 pr-3 font-mono">{row.phone}</td>
                      <td className="py-1.5 pr-3">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-navy/60">
                          {row.context}
                        </span>
                      </td>
                      <td className="py-1.5 pr-3">
                        <Pill ok={!!row.ok}>
                          {row.ok ? "OK" : `${row.status ?? "ERR"}`}
                        </Pill>
                      </td>
                      <td className="py-1.5 pr-3 text-red-700 max-w-[280px] truncate" title={row.error || ""}>
                        {row.error || ""}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Msg91DiagnosticPanel;
